import React, {
    useState,
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback
} from "react";

import LoadingSpinnerImage from "../TaskManagerApp/LoadingSpinnerImage.js";
import ResponsiveIframe from "./ResponsiveIframe.js";
import AddToWatchlistPopup from "./AddToWatchlistPopup.js";
import { getWatchlistTypeFromProfileAlias } from "../Watchlists/profileTypeCatalog.js";
import "../TaskManagerApp/App.css";
import "./App.css";
import { CustomFieldRenderers } from './ProfileFieldDisplays';
import "../Watchlists/App.css";

const ProfilesContext = createContext();

const Profiles = ({ paramProfileType = null, paramProfileSlug = null, paramProfileTab = null }) => {

    const [useDummy, setUseDummy] = useState(false);
    const [isSlimVersion, setIsSlimVersion] = useState(window.isSlimVersion);
    const [currentUserId, setCurrentUserId] = useState(window.currentUserId);
    const [isLoadingPage, setIsLoadingPage] = useState(true);
    const [errorLoadingProfile, setErrorLoadingProfile] = useState(false);

    const [profileData, setProfileData] = useState(null);

    const [profileType, setProfileType] = useState(paramProfileType);
    const [profileSlug, setProfileSlug] = useState(paramProfileSlug);
    const [profileTab, setProfileTab] = useState(paramProfileTab);
    const [initialPath, setInitialPath] = useState("/profiles");
    const [initialContentPath, setInitialContentPath] = useState("/ProfilesDetails");

    const [customTabContent, setCustomTabContent] = useState("");
    const [isLoadingTabContent, setIsLoadingTabContent] = useState(false);
    const [tabContentURL, setTabContentURL] = useState(initialContentPath + "/" + profileType + "/" + profileSlug + "/" + profileTab);
    const [contentType, setContentType] = useState(null);

    const [followStatus, setFollowStatus] = useState(null);

    const activeTabRef = useRef(profileTab);

    useEffect(() => {
        loadData();
    }, []);

    const loadCustomTabContent = () => {
        setIsLoadingTabContent(true);
        let loadDataURL = initialContentPath + "/" + profileType + "/" + profileSlug + "/" + profileTab;

        fetch(loadDataURL, {})
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));
                    throw new Error("Network response was not ok");
                }
                return response.text();
            })
            .then((result) => {
                setCustomTabContent(result);
            })
            .catch((error) => { })
            .finally(() => {
                setIsLoadingTabContent(false);
            });
    }

    const loadData = () => {
        setIsLoadingPage(true);
        let loadDataURL = `/Umbraco/Api/Profile/Profile?profileType=${profileType}&slug=${profileSlug}&tab=${profileTab}`;
        setErrorLoadingProfile(false);

        fetch(loadDataURL, {})
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401)
                        document.dispatchEvent(new Event("api:unauthorized"));
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((result) => {
                if (result) {
                    setProfileData(result);
                    setFollowStatus(result.Following);

                    if (paramProfileTab) {
                        let chosenTab = result.ProfileSections.find(item => item.Slug == paramProfileTab);
                        changeTab(chosenTab);
                    }
                }
            })
            .catch((error) => {
                setErrorLoadingProfile(true);
            })
            .finally(() => {
                setIsLoadingPage(false);
            });
    };

    const changeTab = (tab) => {
        let slug = tab.Slug;

        setContentType(tab.Type);
        setProfileTab(slug);
        activeTabRef.current = slug; 

        setIsLoadingTabContent(true); 

        let newUrl = initialPath + "/" + profileType + "/" + profileSlug + "/" + slug;
        window.history.pushState({}, "", newUrl);

        if (tab.Type == "Image") {
            let tabContentUrl = initialContentPath + "/" + profileType + "/" + profileSlug + "/" + slug;
            let requestOptions = {};

            fetch(tabContentUrl, requestOptions)
                .then(async(response) => {
                    if (!response.ok) {
                        if (response.status === 401)
                            document.dispatchEvent(new Event("api:unauthorized"));

                        throw new Error("Network response was not ok");
                    }
                    const contentType = response.headers.get("content-type") || "";
                    const isHtml = contentType.includes("text/html");
                    const data = await response.text();

                    return { data, isHtml };
                })
                .then(({ data, isHtml }) => {
                    if (isHtml) {
                        tab.Type = "Table";
                        tab.ImageField = null;
                        setContentType(tab.Type);
                        let tabContentUrl = initialContentPath + "/" + profileType + "/" + profileSlug + "/" + slug;
                        setTabContentURL(tabContentUrl);
                        setIsLoadingTabContent(false);
                    } else if (data && activeTabRef.current === slug) {
                        setTabContentURL(data);
                        setIsLoadingTabContent(false);
                    }
                })
                .catch((error) => {
                    console.error("Webservice request failed:", error);
                    if (activeTabRef.current === slug) {
                        setIsLoadingTabContent(false);
                    }
                });
        }
        else {
            let tabContentUrl = initialContentPath + "/" + profileType + "/" + profileSlug + "/" + slug;
            setTabContentURL(tabContentUrl);
            setIsLoadingTabContent(false); 
        }
    }

    let globalContext = {
        useDummy, setUseDummy,
        isSlimVersion, setIsSlimVersion,
        currentUserId, setCurrentUserId,
        profileData, setProfileData,
        profileType, profileSlug, profileTab,
        initialPath, changeTab, followStatus
    };

    let activeSection = null;
    if (profileData && profileData.ProfileSections) {
        activeSection = profileData.ProfileSections.find(s => s.Slug == profileTab);
    }

    return (
        <ProfilesContext.Provider value={globalContext}>
            <div className="profiles-app-wrapper">
                {isLoadingPage ? (
                    <div className="profiles-loading-container">
                        <LoadingSpinnerImage />
                    </div>
                ) : (
                    <>
                        {profileData != null && <Header />}

                        {errorLoadingProfile ? (
                            <div className="container-fluid profiles-container-fluid mt-4">
                                <div className="alert alert-danger profiles-alert" role="alert">
                                    <h4 className="alert-heading">Data Retrieval Error</h4>
                                    <p className="mb-0">Unable to load the regulatory profile for "{profileSlug}". Please refresh the page or contact system administration.</p>
                                </div>
                            </div>
                        ) : profileData != null && (
                            <div className="container-fluid profiles-container-fluid mt-4 pb-5">
                                <div className="row">
                                    {/* LEFT SIDEBAR: Thinner width */}
                                    <div className="col-md-4 col-lg-2 mb-4">
                                        <div className="profiles-sidebar-card">
                                            <h6 className="profiles-sidebar-title">

                                            </h6>
                                            <TabList />
                                        </div>
                                    </div>

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="col-md-8 col-lg-10">

                                        {profileTab === "profile" && profileData.ProfileFields && profileData.ProfileFields.length > 0 && (
                                            <div className="profiles-content-card mb-4">
                                                <CustomFields />
                                            </div>
                                        )}

                                        <div className="profiles-content-card">

                                            {profileTab !== "profile" && activeSection && (
                                                <h4 className="profiles-section-heading d-flex align-items-center">
                                                    {activeSection.Title}
                                                </h4>
                                            )}

                                            {isLoadingTabContent ? (
                                                <div className="profiles-loading-container mt-4">
                                                    <LoadingSpinnerImage />
                                                </div>
                                            ) : (
                                                <div className="profiles-tab-content-area">
                                                    {contentType === "Image" && (
                                                        <div className="text-center">
                                                            <img
                                                                key={profileTab} 
                                                                className="profiles-content-image"
                                                                src={tabContentURL}
                                                                alt="Regulatory document or chart"
                                                            />
                                                        </div>
                                                    )}
                                                    {contentType === "Table" && (
                                                        <div className="profiles-iframe-wrapper">
                                                            <ResponsiveIframe
                                                                key={profileTab} 
                                                                src={tabContentURL}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ProfilesContext.Provider>
    );
}

const Header = () => {
    const AppContext = useContext(ProfilesContext);
    const [isWatchlistPopupOpen, setIsWatchlistPopupOpen] = useState(false);

    let profileId = AppContext.profileData.ProfileId;
    let profileType = AppContext.profileType;
    let profileSlug = AppContext.profileSlug;
    let profileTab = AppContext.profileTab;
    let profileName = AppContext.profileData.ProfileName;
    let profileImage = AppContext.profileData.ProfileImage;
    let profileIcon = AppContext.profileData.ProfileIcon;
    let uniqueId = AppContext.profileData.UniqueId;

    let profileBreadcrumbList = AppContext.profileData.ProfileBreadcrumbList || "";

    let watchlistProfileType = getWatchlistTypeFromProfileAlias(profileType);
    let pageDescription = AppContext.profileData.PageDescription || AppContext.profileData.pageDescription;
    let returnUrl = AppContext.initialPath + "/" + profileType + "/" + profileSlug + "/" + profileTab;
    let displayIcon = profileIcon ? profileIcon : "icon-info";

    return (
        <div className="profiles-header-bg">
            <div className="container-fluid profiles-container-fluid">
                <div>
                    <nav aria-label="breadcrumb" className="mb-3">
                        <ol className="breadcrumb bg-transparent p-0 mb-0" style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>
                            <li className="breadcrumb-item">
                                {profileBreadcrumbList ? (
                                    <a href={profileBreadcrumbList} className="text-muted">
                                        {profileType.replace(/-/g, ' ')}
                                    </a>
                                ) : (
                                    <span className="text-muted">
                                        {profileType.replace(/-/g, ' ')}
                                    </span>
                                )}
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                {profileName}
                            </li>
                        </ol>
                    </nav>                </div>
                <div className="row align-items-center">
                    <div className="col-md-8">
                       <div className="d-flex align-items-center">
                            {profileImage && profileImage.trim() !== "" ? (
                                <img className="profiles-avatar-img" src={profileImage} alt="Profile" />
                            ) : (
                                <div className="profiles-avatar-icon-wrapper">
                                    <i className={`${displayIcon} profiles-avatar-icon`} aria-hidden="true"></i>
                                </div>
                            )}

                            <div>
                                <h2 className="profiles-header-title mb-0">
                                    {profileName}
                                </h2>
                                {pageDescription && (
                                    <div
                                        className="mt-2 profiles-header-description"
                                        dangerouslySetInnerHTML={{ __html: pageDescription }}
                                    />
                                )}
                            </div>
                        </div>

                    </div>

                    <div className="col-md-4 text-md-right mt-3 mt-md-0">
                        <div className="profiles-header-actions">
                            {watchlistProfileType && uniqueId && (
                                <button
                                    type="button"
                                    className="btn profiles-btn-watchlist"
                                    onClick={() => setIsWatchlistPopupOpen(true)}
                                >
                                    <i className="fas fa-binoculars mr-2" aria-hidden="true"></i>Add to Watchlist
                                </button>
                            )}
                            {AppContext.followStatus != null && (
                                AppContext.followStatus === true ? (
                                    <a className="btn profiles-btn-following" href={"/umbraco/surface/newsfeedbutton/Remove/" + profileId + "?type=" + profileType + "&returnUrl=" + returnUrl}>
                                        <i className="icon-check mr-2" aria-hidden="true"></i>Following
                                    </a>
                                ) : (
                                    <a className="btn profiles-btn-follow" href={"/umbraco/surface/newsfeedbutton/Add/" + profileId + "?type=" + profileType + "&returnUrl=" + returnUrl}>
                                        <i className="icon-feed mr-2" aria-hidden="true"></i>Follow
                                    </a>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {watchlistProfileType && uniqueId && (
                <AddToWatchlistPopup
                    isOpen={isWatchlistPopupOpen}
                    onClose={() => setIsWatchlistPopupOpen(false)}
                    profileType={watchlistProfileType}
                    uniqueId={uniqueId}
                    slug={uniqueId}
                    profileName={profileName}
                />
            )}
        </div>
    );
}

const TabList = () => {
    const AppContext = useContext(ProfilesContext);
    let profileTab = AppContext.profileTab;

    return (
        <div className="profiles-nav-list">
            {AppContext.profileData.ProfileSections.map((section, index) => {
                let sectionName = section.Title;
                let count = section.StatsCount;
                let numericCount = Number(count);
                let isActive = section.Slug == profileTab;

                let badgeClass = "profiles-badge";
                if (isActive) {
                    badgeClass += numericCount > 0 ? " profiles-badge-active" : " profiles-badge-active-zero";
                } else {
                    badgeClass += numericCount > 0 ? " profiles-badge-populated" : " profiles-badge-zero";
                }

                return (
                    <button
                        key={index}
                        onClick={() => AppContext.changeTab(section)}
                        className={`profiles-nav-item ${isActive ? "profiles-nav-item-active" : ""}`}
                    >
                        <div className="profiles-nav-item-content">
                            <span className="profiles-nav-text">{sectionName}</span>
                        </div>

                        {count != null && (
                            <span className={badgeClass}>
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

const CustomFields = () => {
    const AppContext = useContext(ProfilesContext);
    let fields = AppContext.profileData.ProfileFields;

    return (
        <div className="profiles-details-section">
            <h4 className="profiles-section-heading d-flex align-items-center">
                Overview
            </h4>
            <div className="profiles-fields-wrapper">
                {fields.map((field, index) => {
                    let fieldType = field.FieldType;
                    let fieldName = field.FieldName;

                    if (field.FieldValue === "") return null;

                    const CustomComponent = CustomFieldRenderers[fieldName];
                    if (CustomComponent) {
                        return <div className="mb-3" key={index}><CustomComponent field={field} /></div>;
                    }

                    if (fieldType === "Text") return <FieldText key={index} field={field} />;
                    else if (fieldType === "Url") return <FieldLink key={index} field={field} />;
                    else if (fieldType === "RelatedObjects") return <FieldRelatedObject key={index} field={field} />;

                    return null;
                })}
            </div>
        </div>
    );
}

const FieldText = ({ field }) => {
    return (
        <div className="profiles-field-row">
            <div className="profiles-field-label">{field.TitleDisplayed}</div>
            <div className="profiles-field-value">{field.FieldValue}</div>
        </div>
    );
}

const FieldLink = ({ field }) => {
    return (
        <div className="profiles-field-row">
            <div className="profiles-field-label">{field.TitleDisplayed}</div>
            <div className="profiles-field-value">
                <a href={field.FieldValue} target="_blank" rel="noopener noreferrer" className="profiles-field-link">
                    {field.FieldValue} <i className="icon-external-link small ml-1"></i>
                </a>
            </div>
        </div>
    );
}

const FieldRelatedObject = ({ field }) => {
    return (
        <div className="profiles-field-row">
            <div className="profiles-field-label">{field.TitleDisplayed}</div>
            <div className="profiles-field-value">
                <ul className="profiles-related-list">
                    {field.FieldValue.map((obj, index) => (
                        <li key={index} className="profiles-related-item">
                            <a href={obj.ProfilePath} className="profiles-field-link d-flex align-items-center">
                                <i className="icon-link mr-2 small text-muted"></i> {obj.ProfileName}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default Profiles;